import { assign, defineMachine } from 'fsm';
import { lowLevelAlertErrorTypes } from './LowLevelAuthErrorAlert';
export function getLowLevelAuthFlowMachineDefinition() {
  return defineMachine()({
    initial: 'flow',
    context: {},
    states: {
      flow: {
        on: {
          GO_TO_NEXT_STEP: {
            target: 'flow',
            cond: 'canGoToNextStep',
            actions: ['goToNextStep']
          },
          GO_TO_PREV_STEP: {
            target: 'flow',
            cond: 'canGoToPrevStep',
            actions: ['goToPrevStep']
          },
          UPDATE_PARAM: {
            actions: ['updateParam']
          },
          UPDATE_SCOPES: {
            actions: ['updateScopes']
          },
          START_AUTH: {
            target: 'authorizing',
            actions: ['startAuthProcess']
          },
          FINISH_FAILURE: [{
            cond: 'isCustomError',
            target: 'customError',
            actions: ['setAuthError', 'reportError']
          }, {
            target: 'flow',
            actions: ['setAuthError', 'reportError']
          }],
          FINISH_SUCCESS: {
            target: 'success',
            actions: ['setAuthSucceeded', 'reportSuccess']
          },
          CLEAR_ERROR: {
            actions: ['clearAuthError']
          }
        }
      },
      authorizing: {
        on: {
          OAUTH_WINDOW_OPENED: {
            actions: ['setOAuthWindow']
          },
          OAUTH_WINDOW_BLOCKED: {
            target: 'flow',
            actions: ['setAuthFailed', 'setOAuthWindowBlockedError']
          },
          OAUTH_WINDOW_TIMEOUT: {
            target: 'flow',
            actions: ['setAuthCancelled', 'setOAuthTimeoutError', 'closeOAuthWindow']
          },
          OAUTH_WINDOW_CLOSED: {
            target: 'flow',
            actions: ['cancelAuthAsWindowClosed']
          },
          START_POLLING: {
            target: 'polling',
            actions: ['setPollingStatus']
          },
          OAUTH_RESPONSE_RECEIVED: [{
            cond: 'isValidOAuthState',
            target: 'authenticating',
            actions: ['setAuthenticatingStatus']
          }, {
            target: 'flow',
            actions: ['setAuthFailed', 'setUnknownOAuthError']
          }],
          FINISH_FAILURE: [{
            cond: 'isCustomError',
            target: 'customError',
            actions: ['setAuthError', 'reportError', 'resetAuthProcess']
          }, {
            target: 'flow',
            actions: ['setAuthError', 'reportError', 'resetAuthProcess']
          }],
          FINISH_SUCCESS: {
            target: 'success',
            actions: ['setAuthSucceeded', 'reportSuccess', 'resetAuthProcess']
          }
        }
      },
      polling: {
        on: {
          POLL_TICK: {
            actions: ['incrementPollingCount']
          },
          POLL_SUCCESS: [{
            cond: 'isPollResponseReady',
            target: 'authenticating',
            actions: ['setAuthenticatingStatus']
          }, {
            target: 'polling'
          }],
          POLL_ERROR: {
            target: 'flow',
            actions: ['setAuthFailed', 'setUnknownOAuthError']
          },
          OAUTH_RESPONSE_RECEIVED: [{
            cond: 'isValidOAuthState',
            target: 'authenticating',
            actions: ['setAuthenticatingStatus']
          }, {
            target: 'flow',
            actions: ['setAuthFailed', 'setUnknownOAuthError']
          }],
          OAUTH_WINDOW_TIMEOUT: {
            target: 'flow',
            actions: ['setAuthCancelled', 'setOAuthTimeoutError', 'closeOAuthWindow']
          },
          // The popup closing is not a reliable signal in OAuthWithPolling: a 3p
          // with restrictive CORS/COOP severs the parent's access to the popup, so
          // `window.closed` can read true while the user is still mid-auth, and the
          // oauth-redirect page deliberately closes the popup after persisting the
          // authorization code for polling to retrieve. Ignore it and keep polling
          // until a poll resolves or the session times out.
          OAUTH_WINDOW_CLOSED: {},
          FINISH_SUCCESS: {
            target: 'success',
            actions: ['setAuthSucceeded', 'reportSuccess', 'resetAuthProcess']
          },
          FINISH_FAILURE: [{
            cond: 'isCustomError',
            target: 'customError',
            actions: ['setAuthError', 'reportError', 'resetAuthProcess']
          }, {
            target: 'flow',
            actions: ['setAuthError', 'reportError', 'resetAuthProcess']
          }]
        }
      },
      authenticating: {
        on: {
          // Ignore polling events while authenticating - polling may still be running
          POLL_TICK: {},
          POLL_SUCCESS: {},
          POLL_ERROR: {},
          // Ignore OAUTH_RESPONSE_RECEIVED - for polling flow, POLL_SUCCESS already transitioned here
          OAUTH_RESPONSE_RECEIVED: {},
          FINISH_SUCCESS: {
            target: 'success',
            actions: ['setAuthSucceeded', 'reportSuccess', 'resetAuthProcess']
          },
          FINISH_FAILURE: [{
            cond: 'isCustomError',
            target: 'customError',
            actions: ['setAuthError', 'reportError', 'resetAuthProcess']
          }, {
            target: 'flow',
            actions: ['setAuthError', 'reportError', 'resetAuthProcess']
          }]
        }
      },
      customError: {
        on: {
          RESET_CUSTOM_ERROR: {
            target: 'flow',
            actions: ['resetAfterCustomError']
          }
        }
      },
      success: {
        on: {
          RESET_FLOW: {
            target: 'flow',
            actions: ['resetFlowState']
          }
        }
      }
    },
    actions: {
      goToNextStep: assign(context => ({
        stepIndex: Math.min(context.stepIndex + 1, context.authConfig.authFlow.steps.length - 1)
      })),
      goToPrevStep: assign(context => ({
        stepIndex: Math.max(context.stepIndex - 1, context.initialStepIndex)
      })),
      updateParam: assign((context, event) => {
        if (event.type !== 'UPDATE_PARAM') {
          return {};
        }
        return {
          params: Object.assign({}, context.params, {
            [event.name]: event.value
          })
        };
      }),
      updateScopes: assign((_context, event) => {
        if (event.type !== 'UPDATE_SCOPES') {
          return {};
        }
        return {
          oAuthScopeOverride: event.oAuthScopes
        };
      }),
      startAuthProcess: assign(() => ({
        authProcessStatus: 'authorizing',
        pollingCount: 0,
        authError: undefined
      })),
      setOAuthWindow: assign((_context, event) => {
        if (event.type !== 'OAUTH_WINDOW_OPENED') {
          return {};
        }
        return {
          oAuthWindow: event.window,
          oAuthState: event.oAuthState
        };
      }),
      setPollingStatus: assign(() => ({
        authProcessStatus: 'polling'
      })),
      setAuthenticatingStatus: assign(() => ({
        authProcessStatus: 'authenticating'
      })),
      incrementPollingCount: assign(context => ({
        pollingCount: context.pollingCount + 1
      })),
      setAuthFailed: assign(() => ({
        authProcessStatus: 'failed'
      })),
      setAuthCancelled: assign(() => ({
        authProcessStatus: 'cancelled'
      })),
      setOAuthWindowBlockedError: assign(() => ({
        authError: {
          type: 'OAuthWindowBlocked'
        }
      })),
      setOAuthTimeoutError: assign(() => ({
        authError: {
          type: 'OAuthWindowTimeout'
        }
      })),
      setUnknownOAuthError: assign(() => ({
        authError: {
          type: 'UnknownOAuthProblem'
        }
      })),
      closeOAuthWindow: context => {
        if (context.oAuthWindow) {
          context.oAuthWindow.close();
        }
      },
      cancelAuthAsWindowClosed: assign(() => ({
        authProcessStatus: 'cancelled',
        authError: {
          type: 'OAuthWindowClosed'
        },
        oAuthWindow: null,
        oAuthState: ''
      })),
      resetAuthProcess: assign(() => ({
        authProcessStatus: 'idle',
        pollingCount: 0,
        oAuthWindow: null,
        oAuthState: ''
      })),
      setAuthSucceeded: assign((_context, event) => {
        if (event.type !== 'FINISH_SUCCESS') {
          return {};
        }
        return {
          authState: {
            status: 'succeeded',
            authResponse: event.response
          },
          authError: undefined,
          authProcessStatus: 'idle'
        };
      }),
      setAuthError: assign((_context, event) => {
        if (event.type !== 'FINISH_FAILURE') {
          return {};
        }
        return {
          authError: event.error,
          authProcessStatus: 'failed'
        };
      }),
      reportSuccess: (context, event) => {
        if (event.type === 'FINISH_SUCCESS') {
          context.onComplete(event.response);
        }
      },
      reportError: (context, event) => {
        if (event.type === 'FINISH_FAILURE') {
          var _context$onError;
          (_context$onError = context.onError) === null || _context$onError === void 0 || _context$onError.call(context, event.error);
        }
      },
      resetAfterCustomError: assign(context => ({
        authError: undefined,
        stepIndex: context.initialStepIndex,
        authProcessStatus: 'idle',
        pollingCount: 0,
        oAuthWindow: null,
        oAuthState: '',
        oAuthScopeOverride: undefined
      })),
      resetFlowState: assign(context => ({
        authError: undefined,
        authState: {
          status: 'pending'
        },
        stepIndex: context.initialStepIndex,
        authProcessStatus: 'idle',
        pollingCount: 0,
        oAuthWindow: null,
        oAuthState: '',
        oAuthScopeOverride: undefined
      })),
      clearAuthError: assign(() => ({
        authError: undefined
      }))
    },
    guards: {
      canGoToNextStep: context => {
        const {
          inline = false,
          steps
        } = context.authConfig.authFlow;
        return !inline && context.stepIndex < steps.length - 1;
      },
      canGoToPrevStep: context => {
        const {
          inline = false
        } = context.authConfig.authFlow;
        return !inline && context.stepIndex > context.initialStepIndex;
      },
      isCustomError: (_context, event) => event.type === 'FINISH_FAILURE' && !lowLevelAlertErrorTypes.includes(event.error.type),
      isValidOAuthState: (context, event) => {
        if (event.type !== 'OAUTH_RESPONSE_RECEIVED') {
          return false;
        }
        return event.state === context.oAuthState;
      },
      isPollResponseReady: (_context, event) => {
        if (event.type !== 'POLL_SUCCESS') {
          return false;
        }
        return event.status !== 'PENDING';
      }
    }
  });
}